import { z } from "zod";

export const loginSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(8, "At least 8 characters"),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const registerSchema = z.object({
  name: z.string().trim().min(2, "Enter your full name"),
  email: z.string().email("Enter a valid email"),
  password: z.string().min(8, "At least 8 characters"),
});
export type RegisterInput = z.infer<typeof registerSchema>;

/**
 * Registration with confirm-password and mobile (single-page form).
 * The API stores first_name/last_name separately, so the single full-name
 * field must contain at least two words for the split to satisfy it.
 */
export const registerWithConfirmSchema = registerSchema
  .extend({
    mobile: z.string().regex(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number"),
    password_confirmation: z.string().min(1, "Confirm your password"),
  })
  .refine((data) => data.name.trim().split(/\s+/).length >= 2, {
    path: ["name"],
    message: "Enter your first and last name",
  })
  .refine((data) => data.password === data.password_confirmation, {
    path: ["password_confirmation"],
    message: "Passwords do not match",
  });
export type RegisterWithConfirmInput = z.infer<typeof registerWithConfirmSchema>;

/** Password reset — step 1: which account to email the reset link to. */
export const forgotPasswordSchema = z.object({
  email: z.string().email("Enter a valid email"),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/**
 * Password reset — step 2: the token + email carried by the emailed link, plus
 * the new password. Upstream is Laravel's password broker, which expects all
 * four fields (token, email, password, password_confirmation).
 */
export const resetPasswordSchema = z
  .object({
    token: z.string().min(1, "This reset link is missing its token"),
    email: z.string().email("Enter a valid email"),
    password: z.string().min(8, "At least 8 characters"),
    password_confirmation: z.string().min(1, "Confirm your password"),
  })
  .refine((data) => data.password === data.password_confirmation, {
    path: ["password_confirmation"],
    message: "Passwords do not match",
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

/** Password reset via OTP — verify the 4-digit code sent for that email's account. */
export const resetOtpVerifySchema = z.object({
  email: z.string().email("Enter a valid email"),
  otp: z.string().length(4, "Enter the 4-digit code"),
});
export type ResetOtpVerifyInput = z.infer<typeof resetOtpVerifySchema>;

/** WhatsApp OTP — step 1: request/resend a code for a 10-digit Indian mobile number. */
export const otpRequestSchema = z.object({
  mobile: z.string().regex(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number"),
});
export type OtpRequestInput = z.infer<typeof otpRequestSchema>;

/** WhatsApp OTP — step 2: verify the 4-digit code sent to that number. */
export const otpVerifySchema = z.object({
  mobile: z.string().regex(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number"),
  otp: z.string().length(4, "Enter the 4-digit code"),
});
export type OtpVerifyInput = z.infer<typeof otpVerifySchema>;
